import React, { useEffect } from 'react';
import '../legal/legal.css';

// ─────────────────────────────────────────────────────────────────────────────
// TheBowlingClub — public landing page (/thebowlingclub) for the tournament
// series „Der Bowlingspieler des Monats“. Structure mirrors the Help Center
// (full-bleed hero below the site header, content sections, contact band), but
// the palette comes from the TheBowlingClub logo instead of the Main Events
// brand colors: yellow / red / white / sky-blue on deep navy (see `se-tbc-*`
// styles in legal.css).
//
// Static German copy on purpose — the page pitches local Braunschweig sponsors,
// same as the sponsoring brochure it links to (public/thebowlingclub/…, see the
// README there for the binary assets this page expects).
// ─────────────────────────────────────────────────────────────────────────────

const TBC_LOGO = '/thebowlingclub/thebowlingclub_logo.png';
const BOWLER_IMG = '/thebowlingclub/bowler.png';
const PDF_URL = '/thebowlingclub/Main-Events-TheBowlingClub.pdf';
const SEUYTEL_LOGO = '/seuytellogo.png';
const MAINEVENTS_LOGO = '/mainevents_logo.svg';

const CONTACT_PRIMARY = 'harold.tela@seuytel.com';
const CONTACT_GENERAL = 'kontakt@seuytel.com';

// Optional binary assets may not be deployed yet — hide the <img> instead of
// showing a broken-image icon.
const hideOnError: React.ReactEventHandler<HTMLImageElement> = (e) => {
    e.currentTarget.style.display = 'none';
};

const LINE_UP = [
    {
        count: '8',
        accent: 'se-tbc-accent-red',
        title: 'Community-Spieler',
        text: 'Spieler bewerben sich über Social Media und unsere Website und werden für das finale Teilnehmerfeld ausgewählt.',
    },
    {
        count: '5',
        accent: 'se-tbc-accent-yellow',
        title: 'Firmen-Startplätze',
        text: 'Startplätze gehen exklusiv an gesponserte Spieler aus lokalen Unternehmen.',
    },
    {
        count: '3',
        accent: 'se-tbc-accent-blue',
        title: 'VIP-Persönlichkeiten',
        text: 'VIP-Persönlichkeiten spielen von Beginn an auf Augenhöhe mit – ohne Favoritenrolle – und zeigen ihren Fans, was sie mit der Kugel draufhaben.',
    },
];

export default function TheBowlingClub() {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'TheBowlingClub – Main Events';
        return () => {
            document.title = prevTitle;
        };
    }, []);

    return (
        <div className="se-tbc">
            {/* hero — big TheBowlingClub logo below the site header */}
            <section className="se-tbc-hero">
                <div className="se-tbc-hero-glow" aria-hidden="true" />
                <div className="se-tbc-hero-inner">
                    <span className="se-tbc-eyebrow">Ein Projekt von Main Events · Seuytel UG</span>
                    <img
                        className="se-tbc-hero-logo"
                        src={TBC_LOGO}
                        alt="TheBowlingClub"
                        onError={hideOnError}
                    />
                    <h1>„Der Bowlingspieler des Monats“</h1>
                    <p className="se-tbc-hero-sub">
                        16 Spieler. Ein K.o.-Turnier. Live vor Ort und Online.
                    </p>
                    <div className="se-tbc-cta-row">
                        <a className="se-tbc-cta" href={PDF_URL} target="_blank" rel="noreferrer">
                            Sponsoring-Broschüre (PDF)
                        </a>
                        <a className="se-tbc-cta se-tbc-cta-ghost" href={`mailto:${CONTACT_PRIMARY}`}>
                            Partner werden
                        </a>
                    </div>
                </div>
            </section>

            <main className="se-content se-tbc-content">
                {/* intro */}
                <section className="se-tbc-section">
                    <p className="se-tbc-lead">
                        Unter dem Dach unserer Event-Plattform <strong>Main Events</strong> entwickeln wir
                        Formate, die Sport, Live-Entertainment und digitale Reichweite miteinander
                        verbinden. Eines davon ist <strong>TheBowlingClub</strong> – und mit „Der
                        Bowlingspieler des Monats“ startet nun unser ambitioniertes Turnierformat.
                    </p>
                    <p>
                        16 Spieler treten in einem packenden K.o.-Turnier gegeneinander an – ein
                        Wettkampf, der sich live vor Ort ebenso mitreißend erleben lässt wie im
                        Online-Stream. Im Anschluss stellen wir das gesamte Event zusätzlich auf YouTube
                        bereit, damit noch mehr Fans mitfiebern können.
                    </p>
                    <p>
                        Dass die Premiere in Braunschweig stattfindet, ist kein Zufall: Als zweitgrößte
                        Stadt Niedersachsens vereint sie eine lebendige Sportkultur, einen starken lokalen
                        Mittelstand und eine große, engagierte Community. Braunschweig ist für uns nicht
                        bloß die erste von vielen Stationen, sondern das Herz der Serie – und zugleich der
                        Heimatstandort unseres Unternehmens. Wer heute Partner wird, schreibt an einer
                        Geschichte mit, die weit über die Stadtgrenzen hinauswirkt, und setzt ein
                        sichtbares Zeichen für sein Engagement in Braunschweig und für seine eigene
                        Zukunft.
                    </p>
                </section>

                {/* brand strip — Seuytel → Main Events → TheBowlingClub */}
                <section className="se-tbc-section">
                    <h2>Eine Familie, drei Marken</h2>
                    <div className="se-tbc-brands">
                        <div className="se-tbc-brand">
                            <img src={SEUYTEL_LOGO} alt="Seuytel UG" onError={hideOnError} />
                        </div>
                        <span className="se-tbc-brand-link" aria-hidden="true">›</span>
                        <div className="se-tbc-brand">
                            <img src={MAINEVENTS_LOGO} alt="Main Events" onError={hideOnError} />
                        </div>
                        <span className="se-tbc-brand-link" aria-hidden="true">›</span>
                        <div className="se-tbc-brand">
                            <img src={TBC_LOGO} alt="TheBowlingClub" onError={hideOnError} />
                        </div>
                    </div>
                    <p className="se-tbc-brand-caption">
                        <strong>TheBowlingClub</strong> ist ein Projekt von{' '}
                        <strong>Main Events</strong>, die Event-Plattform der{' '}
                        <strong>Seuytel UG</strong>, Braunschweig.
                    </p>
                </section>

                {/* about Main Events / TheBowlingClub */}
                <section className="se-tbc-section">
                    <h2>Über uns</h2>
                    <div className="se-tbc-cards">
                        <div className="se-tbc-card">
                            <h3>Main Events</h3>
                            <p>
                                <strong>Main Events</strong> ist eine innovative, soziale Event-Plattform und
                                eine Marke der im September 2025 gegründeten <strong>Seuytel UG</strong>. Die
                                All-in-one-Software kombiniert nahtlos Ticketing, Kalender- und
                                Terminverwaltung sowie interaktive Kommunikationskanäle für Teilnehmer.
                                Gleichzeitig bietet sie Veranstaltern ein mächtiges Dashboard zur flexiblen
                                Steuerung von Gästelisten, Hosts und der gesamten Event-Performance.
                            </p>
                        </div>
                        <div className="se-tbc-card">
                            <h3>TheBowlingClub</h3>
                            <p>
                                Um die Gesellschaft zu bewegen, investiert <strong>Main Events</strong> in{' '}
                                <strong>TheBowlingClub</strong> – unser neuestes Turnierformat:{' '}
                                <strong>„Der Bowlingspieler des Monats“</strong>. Es verbindet sportlichen
                                Ehrgeiz mit einem echten Ort der Begegnung: Wir bringen die Community vor Ort
                                zusammen und lassen zugleich ein großes Online-Publikum live daran teilhaben.
                                Dieses besondere Erlebnis tragen wir anschließend von Stadt zu Stadt – für
                                lokale wie digitale Partner eine reichweitenstarke Bühne, um sowohl vor Ort
                                als auch im Online sichtbar zu sein.
                            </p>
                        </div>
                    </div>
                </section>

                {/* tournament format */}
                <section className="se-tbc-section">
                    <h2>Das Turnier</h2>
                    <div className="se-tbc-split">
                        <div>
                            <p>
                                <strong>„Der Bowlingspieler des Monats“</strong> ist ein hochemotionales
                                K.o.-Turnier, dessen Premiere für die dritte Augustwoche geplant ist. 16
                                Spieler treten im direkten Duell gegeneinander an: Achtel-, Viertel- und
                                Halbfinale mit je 30 Minuten Spielzeit, das große Finale über 45 Minuten. Das
                                Event verbindet Sport mit Live-Entertainment und wird sowohl vor Ort als auch
                                zeitgleich online übertragen – vor einem Publikum, das von mehreren Tausend
                                bis in den Millionenbereich reichen kann. Braunschweig gibt dabei den
                                Startschuss: als erste Ausgabe einer groß angelegten Serie, die anschließend
                                von Stadt zu Stadt weiterzieht.
                            </p>
                            <p>
                                Das Line-up ist strategisch so konzipiert, dass maximale Reichweite und
                                lokales Engagement zusammenkommen:
                            </p>
                        </div>
                        <img
                            className="se-tbc-split-img"
                            src={BOWLER_IMG}
                            alt="TheBowlingClub – Bowling-Pin-Charakter auf der Bahn"
                            onError={hideOnError}
                        />
                    </div>

                    <div className="se-tbc-lineup">
                        {LINE_UP.map((item) => (
                            <div key={item.title} className={`se-tbc-lineup-card ${item.accent}`}>
                                <span className="se-tbc-lineup-count">{item.count}</span>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </div>
                        ))}
                    </div>

                    <p>
                        Das Format bietet Sponsoren eine einzigartige Bühne mitten im Geschehen. Der Mix
                        aus Sport, Entertainment und bekannten Gesichtern erzeugt emotionalen Content mit
                        massiver digitaler Reichweite im Stream. Lokale Partner müssen sich dabei nicht
                        auf klassische Präsenz beschränken: Über die fünf exklusiven Firmen-Startplätze
                        werden sie selbst aktiver Teil des Turniers – und tragen dazu bei, die Region zu bewegen.
                    </p>
                </section>

                {/* sponsoring brochure */}
                <section className="se-tbc-section">
                    <div className="se-tbc-pdf">
                        <span className="se-tbc-pdf-icon" aria-hidden="true">
                            <svg viewBox="0 0 64 64" role="img">
                                <rect x="14" y="6" width="36" height="52" rx="6" fill="#fff" />
                                <path d="M40 6 v12 h10" fill="none" stroke="#0e1f45" strokeWidth="3" />
                                <line x1="22" y1="28" x2="42" y2="28" stroke="#e12e2e" strokeWidth="4" strokeLinecap="round" />
                                <line x1="22" y1="37" x2="42" y2="37" stroke="#2fa8e1" strokeWidth="4" strokeLinecap="round" />
                                <line x1="22" y1="46" x2="34" y2="46" stroke="#ffc400" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                        </span>
                        <div className="se-tbc-pdf-body">
                            <h2>Sponsoring &amp; Partnerschaften</h2>
                            <p>
                                Vom Präsenz- über den Player- bis zum Outfit-Sponsoring: Mehr Details zu
                                unserem Sponsoring-Programm finden Sie hier.
                            </p>
                            <a className="se-tbc-cta" href={PDF_URL} target="_blank" rel="noreferrer">
                                Broschüre öffnen (PDF)
                            </a>
                        </div>
                    </div>
                </section>

                {/* contact */}
                <section className="se-tbc-section">
                    <div className="se-tbc-contact">
                        <h2>Kontakt</h2>
                        <p>
                            Sie möchten Partner werden oder haben Fragen zum Turnier? Schreiben Sie uns –
                            gerne besprechen wir die Details bei einem persönlichen Termin vor Ort oder in
                            einem Online-Meeting.
                        </p>
                        <div className="se-tbc-mail-row">
                            <a className="se-tbc-mail" href={`mailto:${CONTACT_PRIMARY}`}>
                                ✉ {CONTACT_PRIMARY}
                                <small>Harold Tela</small>
                            </a>
                            <a className="se-tbc-mail" href={`mailto:${CONTACT_GENERAL}`}>
                                ✉ {CONTACT_GENERAL}
                                <small>Allgemeine Anfragen</small>
                            </a>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
